// ---------------------------------------------------------------------------
// C2: AI Bar Ask Panel
// ---------------------------------------------------------------------------
// Expands upward from AI bar showing answer with data, links, action buttons.
// Numbers animate. Links navigate.
// ---------------------------------------------------------------------------
import { useNavigate } from "react-router";
import { ExternalLink, ArrowRight, Sparkles, AlertCircle, ThumbsDown, ThumbsUp } from "lucide-react";
import { Skeleton } from "@/components/skeleton";
import { sendAiFeedbackSignal } from "@/lib/ai-feedback";
import { formatNumber } from "@/lib/i18n";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AskResult {
  content: string;
  data?: Array<{ label: string; value: string | number; trend?: "up" | "down" | "stable" }>;
  actions?: Array<{ label: string; href?: string; action?: string }>;
  sources?: Array<{ label: string; href: string }>;
}

interface AskPanelProps {
  result: AskResult;
  isLoading: boolean;
  error?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AskPanel({ result, isLoading, error }: AskPanelProps) {
  const navigate = useNavigate();

  const handleFeedback = async (verdict: "positive" | "negative") => {
    try {
      await sendAiFeedbackSignal({
        signalType: "ai_bar.response.feedback",
        aiOutputId: `ai-bar:${result.content.slice(0, 80)}`,
        aiOutput: {
          content: result.content,
          data: result.data ?? [],
          actions: result.actions ?? [],
          sources: result.sources ?? [],
        },
        humanAction: {
          verdict,
        },
        context: {
          touchpoint: "ai_bar.ask_panel",
        },
      });
    } catch {
      // Feedback capture should not block the operator path.
    }
  };

  if (error) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rounded-lg border border-border bg-muted/30 p-2.5">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="mt-2 h-6 w-10" />
              <Skeleton className="mt-2 h-3 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-h-80 overflow-y-auto">
      {/* AI label */}
      <div className="flex items-center gap-1.5 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-[var(--color-ai)]" />
        <span className="text-[10px] font-medium text-[var(--color-ai)]">AI-generated</span>
      </div>

      {/* Main content */}
      <p className="text-sm text-foreground leading-relaxed">{result.content}</p>

      {/* Data metrics */}
      {result.data && result.data.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {result.data.map((item) => (
            <div key={item.label} className="stagger-item rounded-lg border border-border bg-muted/30 p-2.5">
              <div className="text-[10px] text-muted-foreground">{item.label}</div>
              <div className="mt-0.5 text-lg font-bold text-foreground tabular-nums count-up">
                {typeof item.value === "number" ? formatNumber(item.value) : item.value}
              </div>
              {item.trend && (
                <div className={`text-[10px] ${
                  item.trend === "up" ? "text-[var(--color-success)]" : item.trend === "down" ? "text-[var(--color-destructive)]" : "text-muted-foreground"
                }`}>
                  {item.trend === "up" ? "↑" : item.trend === "down" ? "↓" : "→"} vs last period
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      {result.actions && result.actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {result.actions.map((action) => (
            <button
              key={action.label}
              onClick={() => {
                if (action.href) navigate(action.href);
              }}
              className="stagger-item inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90"
            >
              {action.label}
              <ArrowRight className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      {/* Source links */}
      {result.sources && result.sources.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {result.sources.map((source) => (
            <button
              key={source.label}
              onClick={() => navigate(source.href)}
              className="stagger-item inline-flex items-center gap-1 text-[10px] text-muted-foreground transition-colors hover:text-[var(--color-primary)]"
            >
              <ExternalLink className="h-3 w-3" />
              {source.label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 border-t border-border/70 pt-3">
        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Feedback</span>
        <button
          type="button"
          onClick={() => void handleFeedback("positive")}
          className="stagger-item inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-[var(--color-success)]/10 hover:text-[var(--color-success)]"
        >
          <ThumbsUp className="h-3 w-3" />
          Helpful
        </button>
        <button
          type="button"
          onClick={() => void handleFeedback("negative")}
          className="stagger-item inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-[var(--color-destructive)]/10 hover:text-[var(--color-destructive)]"
        >
          <ThumbsDown className="h-3 w-3" />
          Off target
        </button>
      </div>
    </div>
  );
}
